import React, { useState, useEffect } from 'react';
import {
    listPaymentTerminals,
    createPaymentTerminal,
    updatePaymentTerminal,
    deletePaymentTerminal,
} from '../services/paymentTerminalsApi';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
    CreditCard,
    Plus,
    Pencil,
    Trash,
    SpinnerGap,
    FloppyDisk,
    Percent,
} from '@phosphor-icons/react';

const emptyForm = { name: '', commission_percent: 0, is_active: true };

const PaymentTerminalsPage = () => {
    const [terminals, setTerminals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const data = await listPaymentTerminals();
            setTerminals(data);
        } catch (e) {
            toast.error('No se pudieron cargar los TPVs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setShowModal(true);
    };

    const openEdit = (terminal) => {
        setEditing(terminal);
        setForm({
            name: terminal.name,
            commission_percent: terminal.commission_percent || 0,
            is_active: terminal.is_active,
        });
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await updatePaymentTerminal(editing.id, form);
                toast.success('TPV actualizado');
            } else {
                await createPaymentTerminal(form);
                toast.success('TPV creado');
            }
            setShowModal(false);
            await fetchAll();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (terminal) => {
        if (!window.confirm(`¿Desactivar el TPV "${terminal.name}"?\n\nLas ventas existentes mantendrán la referencia. Esta acción no elimina datos históricos.`)) {
            return;
        }
        try {
            await deletePaymentTerminal(terminal.id);
            toast.success('TPV desactivado');
            fetchAll();
        } catch (e) {
            toast.error('No se pudo desactivar');
        }
    };

    if (loading) {
        return (
            <div className="p-8 text-center text-[#8B8474]">
                <SpinnerGap size={28} className="animate-spin inline mr-2" />
                Cargando TPVs…
            </div>
        );
    }

    const activeCount = terminals.filter((t) => t.is_active).length;

    return (
        <div className="space-y-6" data-testid="payment-terminals-page">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.18em] text-[#8B8474] font-semibold mb-1">
                        Configuración
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#1F2D4B] flex items-center gap-2">
                        <CreditCard size={28} weight="duotone" className="text-[#FB7185]" />
                        Terminales de pago (TPVs)
                    </h1>
                    <p className="text-sm text-[#4B5878] mt-1">
                        Gestiona el catálogo de terminales bancarias para cobros con tarjeta.
                        La comisión se usa para conciliar con el banco.
                    </p>
                </div>
                <Button
                    onClick={openCreate}
                    data-testid="add-tpv-btn"
                    className="h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md"
                >
                    <Plus size={14} weight="bold" className="mr-1.5" />
                    Añadir TPV
                </Button>
            </div>

            <div className="flex gap-3 flex-wrap">
                <Badge variant="outline" className="bg-white border-[#ECE3D2] text-[#1F2D4B]">
                    {terminals.length} total
                </Badge>
                <Badge variant="outline" className="bg-[#F4ECE0] border-[#ECE3D2] text-[#1F2D4B]">
                    {activeCount} activos
                </Badge>
            </div>

            {terminals.length === 0 ? (
                <Card className="bg-white border border-dashed border-[#ECE3D2]">
                    <CardContent className="p-10 text-center">
                        <CreditCard size={48} weight="duotone" className="mx-auto text-[#C8B996] mb-3" />
                        <p className="text-[#4B5878] mb-4">
                            Aún no has registrado ningún TPV.
                        </p>
                        <Button
                            onClick={openCreate}
                            className="h-10 bg-[#1F2D4B] hover:bg-[#2E3F60] text-white rounded-md"
                            data-testid="add-first-tpv-btn"
                        >
                            <Plus size={14} weight="bold" className="mr-1.5" />
                            Añadir el primero
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {terminals.map((t) => (
                        <Card
                            key={t.id}
                            className={`bg-white border-2 transition-colors ${
                                t.is_active ? 'border-[#ECE3D2] hover:border-[#1F2D4B]' : 'border-[#F4ECE0] opacity-60'
                            }`}
                            data-testid={`tpv-card-${t.id}`}
                        >
                            <CardContent className="p-4 space-y-3">
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <h3 className="font-bold text-[#1F2D4B] text-lg">{t.name}</h3>
                                        <p className="text-xs text-[#8B8474] mt-0.5 flex items-center gap-1">
                                            <Percent size={11} weight="bold" />
                                            Comisión: {Number(t.commission_percent || 0).toFixed(2)}%
                                        </p>
                                    </div>
                                    {!t.is_active && (
                                        <Badge variant="outline" className="bg-[#FEF3C7] border-[#FCD34D] text-[#92400E] text-[10px]">
                                            Inactivo
                                        </Badge>
                                    )}
                                </div>
                                <div className="flex gap-2 pt-2 border-t border-[#F4ECE0]">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openEdit(t)}
                                        data-testid={`edit-tpv-${t.id}`}
                                        className="flex-1 h-8 text-xs border-[#ECE3D2] text-[#1F2D4B] hover:bg-[#F4ECE0]"
                                    >
                                        <Pencil size={12} weight="bold" className="mr-1" />
                                        Editar
                                    </Button>
                                    {t.is_active && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleDelete(t)}
                                            data-testid={`delete-tpv-${t.id}`}
                                            className="h-8 text-xs border-[#FCD7D7] text-[#B91C1C] hover:bg-[#FEE2E2]"
                                        >
                                            <Trash size={12} weight="bold" />
                                        </Button>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="sm:max-w-md" data-testid="tpv-modal">
                    <DialogHeader>
                        <DialogTitle className="text-[#1F2D4B] flex items-center gap-2">
                            <CreditCard size={20} weight="duotone" className="text-[#FB7185]" />
                            {editing ? 'Editar TPV' : 'Nuevo TPV'}
                        </DialogTitle>
                        <DialogDescription className="text-[#8B8474]">
                            {editing ? 'Actualiza los datos del TPV.' : 'Registra una nueva terminal bancaria.'}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold">
                                Nombre *
                            </Label>
                            <Input
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Ej. Banamex, Clip, Mercado Pago"
                                required
                                data-testid="tpv-name-input"
                                className="mt-1 border-[#ECE3D2] focus:border-[#1F2D4B]"
                            />
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-wider text-[#8B8474] font-semibold">
                                Comisión (%) — para conciliación bancaria
                            </Label>
                            <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max="100"
                                value={form.commission_percent}
                                onChange={(e) => setForm({ ...form, commission_percent: parseFloat(e.target.value) || 0 })}
                                placeholder="Ej. 2.50"
                                data-testid="tpv-commission-input"
                                className="mt-1 border-[#ECE3D2] focus:border-[#1F2D4B]"
                            />
                            <p className="text-[11px] text-[#8B8474] mt-1">
                                Solo visible para administradores. Las promotoras nunca ven este dato.
                            </p>
                        </div>
                        <div className="flex items-center justify-between p-3 rounded-md bg-[#F9F7F2] border border-[#ECE3D2]">
                            <div>
                                <Label className="text-sm font-semibold text-[#1F2D4B]">
                                    Activo
                                </Label>
                                <p className="text-xs text-[#8B8474]">
                                    Disponible en el dropdown de checkout.
                                </p>
                            </div>
                            <Switch
                                checked={form.is_active}
                                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
                                data-testid="tpv-active-switch"
                            />
                        </div>
                        <div className="flex gap-2 justify-end pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setShowModal(false)}
                                className="border-[#ECE3D2] text-[#4B5878]"
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="submit"
                                disabled={saving}
                                data-testid="save-tpv-btn"
                                className="bg-[#1F2D4B] hover:bg-[#2E3F60] text-white"
                            >
                                {saving ? (
                                    <SpinnerGap size={14} className="animate-spin mr-1.5" />
                                ) : (
                                    <FloppyDisk size={14} weight="bold" className="mr-1.5" />
                                )}
                                {editing ? 'Actualizar' : 'Crear'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PaymentTerminalsPage;
